import {
    collection as fsCollection,
    doc as fsDoc,
    documentId,
    getDoc,
    getDocs,
    limit as fsLimit,
    orderBy as fsOrderBy,
    query as fsQuery,
    startAfter as fsStartAfter,
    startAt as fsStartAt,
    where as fsWhere,
} from 'firebase/firestore';
import { createEntityCache, cacheKey } from './cache.js';
import { ARCHIVABLE_COLLECTIONS, ARCHIVE_FLAGGED_COLLECTIONS, DEFAULT_CACHABLE_COLLECTIONS } from './collectionTypes.js';

const DEFAULT_SEARCH_LIMIT = 50;
// Upper bound for getAllEntitiesWhere so a runaway query can't page forever.
const DEFAULT_MAX_ALL_DOCS = 5000;

function serializeQueryValue(value) {
    if (value && typeof value === 'object') {
        if ('id' in value) return value.id;
        if ('path' in value) return value.path;
        if (value instanceof Date) return value.toISOString();
        if (typeof value.toString === 'function') return value.toString();
    }
    return value;
}

function buildWhereCacheKey(namespace, collection, data, searchLimit) {
    return JSON.stringify({
        namespace,
        collection,
        needles: data?.needles?.map(n => ({
            field: n.field,
            operator: n.operator,
            match: serializeQueryValue(n.match),
        })) || null,
        orderBy: data?.orderBy ? {
            field: data.orderBy.field,
            direction: data.orderBy.direction || 'asc',
        } : null,
        startAfter: serializeQueryValue(data?.startAfter) || null,
        startAt: serializeQueryValue(data?.startAt) || null,
        limit: data?.limit || searchLimit,
    });
}

export function isArchived(snapshot) {
    return snapshot?.data()?.archived === true;
}

/**
 * Present a QuerySnapshot with archived documents removed.
 */
export function snapshotWithoutArchived(snapshot) {
    const docs = snapshot.docs.filter(doc => !isArchived(doc));
    if (docs.length === snapshot.docs.length) return snapshot;

    return new Proxy(snapshot, {
        get(target, prop) {
            switch (prop) {
                case 'docs': return docs;
                case 'size': return docs.length;
                case 'empty': return docs.length === 0;
                case 'forEach': return (callback, thisArg) => docs.forEach(callback, thisArg);
                default: {
                    // Snapshot getters read internal slots, so the receiver has
                    // to be the real snapshot rather than the proxy.
                    const value = Reflect.get(target, prop, target);
                    return typeof value === 'function' ? value.bind(target) : value;
                }
            }
        },
    });
}

/**
 * Create a reader bound to a single Firestore instance.
 *
 * @param {object} params
 * @param {import('firebase/firestore').Firestore} params.db - Firestore instance.
 * @param {string} params.namespace - Unique id (e.g. 'default' or 'tap-db') used in cache keys.
 * @param {string[]} [params.cachableCollections] - Collections whose entities are cached.
 * @param {number} [params.searchLimit]
 * @param {() => boolean} [params.canSkipNeedlesCheck] - If true when called, getEntitiesWhere
 *        allows queries without needles. Used by Education admin roles; defaults to false.
 * @param {(key: string) => void} [params.onCall] - Telemetry hook (e.g. for request counters).
 * @param {boolean} [params.disableCache]
 * @param {object} [params.cache] - Pre-built cache (see createEntityCache). Defaults to a new one.
 * @param {object} [params.whereCache] - Pre-built object cache for where-query results.
 * @param {() => boolean} [params.canReadArchived] - Whether security rules let
 *        the signed-in user read archived docs. When false, reads of
 *        archivable collections carry `where('archived', '==', false)` so the
 *        query satisfies the rules; when true, archived rows are fetched and
 *        filtered client-side unless `includeArchived` is set.
 */
export function createEntityReader({
    db,
    namespace,
    cachableCollections = DEFAULT_CACHABLE_COLLECTIONS,
    searchLimit = DEFAULT_SEARCH_LIMIT,
    canSkipNeedlesCheck = () => false,
    onCall = () => {},
    disableCache = false,
    cache = createEntityCache(),
    whereCache = {},
    archivableCollections = ARCHIVABLE_COLLECTIONS,
    archiveFlaggedCollections = ARCHIVE_FLAGGED_COLLECTIONS,
    canReadArchived = () => true,
}) {
    if (!db) throw new Error('createEntityReader: db is required');
    if (!namespace) throw new Error('createEntityReader: namespace is required');

    const cachable = new Set(cachableCollections);
    const archivable = new Set(archivableCollections);
    const archiveFlagged = new Set(archiveFlaggedCollections);

    // Archiving is a soft delete
    function shouldHideArchived(collectionName, data) {
        return (archivable.has(collectionName) || archiveFlagged.has(collectionName))
            && !data?.includeArchived;
    }

    // Rules reject a non-admin list query unless it provably excludes
    // archived docs, so the filter has to be part of the query itself.
    function needsServerArchiveFilter(collectionName) {
        return archivable.has(collectionName) && !canReadArchived();
    }

    function keyFor(collection, id) {
        return cacheKey(namespace, collection, id);
    }

    function getEntity(data, collectionName) {
        if (!collectionName) throw new Error('No collection name provided');
        return new Promise((resolve, reject) => {
            if (!data?.id) {
                reject(new Error('No entity ID provided'));
                return;
            }

            const key = keyFor(collectionName, data.id);
            const cached = !disableCache && !data.noCache && cache.read(key);
            if (cached) {
                onCall('getEntity_cache_0');
                resolve(cached);
                return;
            }

            onCall('getEntity_0');
            getDoc(fsDoc(db, `${collectionName}/${data.id}`)).then(res => {
                if (res.exists()) {
                    res.metadata.collection = collectionName;
                    if (!disableCache && !data.noCache && cachable.has(collectionName)) {
                        cache.write(key, res);
                    }
                    resolve(res);
                } else {
                    reject(new Error(`No entity found::getEntity:${data.id}|${collectionName}`));
                }
            }).catch(err => {
                console.error('getEntity error', data, collectionName);
                reject(err);
            });
        });
    }

    /**
     * @param {string[]} ids
     * @param {string} collectionName
     * @param {{includeArchived?: boolean}} [options] - `includeArchived` keeps
     *        soft-deleted rows in the result; only the data browser's
     *        "Show archived" view should pass it.
     */
    function getEntities(ids, collectionName, options) {
        if (!collectionName) throw new Error('No collection name provided');
        const hideArchived = shouldHideArchived(collectionName, options);
        const chunkSize = 10; // Firestore `in` query limit
        const chunks = [];
        const missingIds = [];
        const cachedDocs = new Map();

        ids.forEach(id => {
            const key = keyFor(collectionName, id);
            const cached = disableCache ? null : cache.read(key);
            if (cached) {
                cachedDocs.set(id, cached);
            } else {
                missingIds.push(id);
            }
        });

        for (let i = 0; i < missingIds.length; i += chunkSize) {
            chunks.push(missingIds.slice(i, i + chunkSize));
        }

        const serverFilter = needsServerArchiveFilter(collectionName);
        const queries = chunks.map(chunk => getDocs(fsQuery(
            fsCollection(db, collectionName),
            fsWhere(documentId(), 'in', chunk),
            ...(serverFilter ? [fsWhere('archived', '==', false)] : [])
        )));

        return Promise.all(queries).then(results => {
            const idToDoc = new Map();
            results.forEach(snapshot => {
                snapshot.docs.forEach(d => {
                    d.metadata.collection = collectionName;
                    idToDoc.set(d.id, d);
                    cache.write(keyFor(collectionName, d.id), d);
                });
            });

            const ordered = [];
            ids.forEach(id => {
                if (cachedDocs.has(id)) {
                    ordered.push(cachedDocs.get(id));
                } else if (idToDoc.has(id)) {
                    ordered.push(idToDoc.get(id));
                } else {
                    onCall('getEntities_missing');
                    console.error('Entity not found in batch fetch:', { id, collectionName });
                }
            });

            // Filtered here rather than before caching so the raw document
            // stays available to callers that ask for archived rows.
            return hideArchived ? ordered.filter(doc => !isArchived(doc)) : ordered;
        });
    }

    function getEntitiesWhere(data, collectionName) {
        if (!collectionName) throw new Error('No collection name provided');
        data = data || {};
        const hideArchived = shouldHideArchived(collectionName, data);
        return new Promise((resolve, reject) => {
            const q = [];

            const collectionsWhichDoNotRequireNeedles = new Set([
                'roles',
            ]);

            const hasNeedles = Array.isArray(data.needles) && data.needles.length > 0;
            if (!collectionsWhichDoNotRequireNeedles.has(collectionName) && !hasNeedles && !canSkipNeedlesCheck()) {
                reject(new Error(`No needles data for query on ${collectionName}`));
                return;
            }

            let hasTypes = false;
            data?.needles?.forEach(n => {
                if (n.field === 'type') hasTypes = true;
                q.push(fsWhere(n.field === 'id' ? documentId() : n.field, n.operator, n.match));
            });

            const serverFilter = needsServerArchiveFilter(collectionName);
            if (serverFilter && !data?.needles?.some(n => n.field === 'archived')) {
                q.push(fsWhere('archived', '==', false));
            }

            if (!hasTypes && data.enforceTypes) {
                reject(new Error(`No type in needles for query on ${collectionName}`));
                return;
            }

            if (data.orderBy?.field) {
                q.push(fsOrderBy(data.orderBy.field, data.orderBy.direction || 'asc'));
            }
            if (data.startAfter) q.push(fsStartAfter(data.startAfter));
            if (data.startAt) q.push(fsStartAt(data.startAt));

            let key = null;
            try {
                key = buildWhereCacheKey(namespace, collectionName, data, searchLimit)
                    + (serverFilter ? '|active' : '');
            } catch (err) {
                console.warn('Could not build cache key for getEntitiesWhere', err);
            }

            if (key && !disableCache && !data.noCache && whereCache[key]) {
                onCall('getEntitiesWhere_cache_0');
                resolve(hideArchived ? snapshotWithoutArchived(whereCache[key]) : whereCache[key]);
                return;
            }

            onCall('getEntitiesWhere_0');
            getDocs(fsQuery(
                fsCollection(db, collectionName),
                ...q,
                fsLimit(data.limit || searchLimit)
            )).then(res => {
                // The unfiltered snapshot is what gets cached, so a later
                // `includeArchived` read is still served from cache.
                if (key) whereCache[key] = res;
                res.metadata.collection = collectionName;
                resolve(hideArchived ? snapshotWithoutArchived(res) : res);
            }).catch(reject);
        });
    }

    /**
     * Fetch every document matching a where query, regardless of `searchLimit`.
     *
     * Pages through `getEntitiesWhere` with a document cursor until a page
     * comes back short, so each request stays bounded while the result is
     * complete. Takes the same `data` as `getEntitiesWhere` plus:
     *  - `pageSize` — documents per request (defaults to `searchLimit`).
     *  - `maxDocs` — hard stop on the total fetched (defaults to 5000).
     * `limit` is ignored; use `getEntitiesWhere` for a capped read.
     *
     * Archived rows are filtered once the full set is in hand rather than per
     * page, so they never shift the cursor or eat into a page.
     *
     * Resolves to a snapshot-like `{ docs, size, empty, forEach, metadata }`.
     */
    async function getAllEntitiesWhere(data, collectionName) {
        if (!collectionName) throw new Error('No collection name provided');
        data = data || {};
        const pageSize = data.pageSize || searchLimit;
        const maxDocs = data.maxDocs || DEFAULT_MAX_ALL_DOCS;
        const hideArchived = shouldHideArchived(collectionName, data);

        const pageData = { ...data, includeArchived: true };
        delete pageData.pageSize;
        delete pageData.maxDocs;
        delete pageData.limit;

        onCall('getAllEntitiesWhere_0');

        const docs = [];
        let cursor = data.startAfter || null;
        while (docs.length < maxDocs) {
            const limit = Math.min(pageSize, maxDocs - docs.length);
            const page = await getEntitiesWhere({
                ...pageData,
                limit,
                ...(cursor ? { startAfter: cursor } : {}),
            }, collectionName);

            docs.push(...page.docs);
            if (page.docs.length < limit) break;
            cursor = page.docs[page.docs.length - 1];
        }

        const result = hideArchived ? docs.filter(doc => !isArchived(doc)) : docs;
        return {
            docs: result,
            size: result.length,
            empty: result.length === 0,
            forEach: (callback, thisArg) => result.forEach(callback, thisArg),
            metadata: { collection: collectionName },
        };
    }

    function invalidateEntity(id, collectionName) {
        cache.remove(keyFor(collectionName, id));
    }

    function invalidateWhereCache() {
        for (const k of Object.keys(whereCache)) delete whereCache[k];
    }

    return {
        getEntity,
        getEntities,
        getEntitiesWhere,
        getAllEntitiesWhere,
        invalidateEntity,
        invalidateWhereCache,
        cache,
        whereCache,
    };
}
